import React, { useState, useRef, useEffect } from 'react'
import { translateWordAPI } from '../../services/api'
import {
  type KanaSuggestion,
  buildKanaSuggestions,
  normalizeWordForTranslation,
} from '../../utils/japaneseText'
import {
  type SpanishSuggestion,
  buildSpanishSuggestions,
} from '../../utils/spanishText'
import {
  type MandarinSuggestion,
  buildMandarinSuggestions,
  containsChinese,
} from '../../utils/mandarinText'
import { useLanguageStore } from '../../store/languageStore'

// Unified suggestion type for all languages
type Suggestion =
  | (KanaSuggestion & { language: 'ja' })
  | (SpanishSuggestion & { language: 'es' })
  | (MandarinSuggestion & { language: 'zh' })

interface LanguageInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  disabled?: boolean
  userId?: string | number
  sessionId?: string | number
}

type TranslationStatus = {
  loading?: boolean
  translation?: string
  error?: boolean
}

export function LanguageInput({
  value,
  onChange,
  onSubmit,
  placeholder = 'Type your message...',
  disabled = false,
  userId,
  sessionId,
}: LanguageInputProps) {
  const currentLanguageCode = useLanguageStore((s) => s.currentLanguage)
  const supportsAutocomplete = ['ja', 'es', 'zh'].includes(currentLanguageCode || '')

  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [translationState, setTranslationState] = useState<Record<string, TranslationStatus>>({})
  const [isComposing, setIsComposing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const translationCacheRef = useRef<Record<string, string>>({})
  const canonicalTranslationCacheRef = useRef<Record<string, string>>({})
  const loadingCanonicalsRef = useRef<Set<string>>(new Set())

  // Get the current word being typed (from cursor position)
  const getCurrentWord = () => {
    if (!inputRef.current) return { word: '', start: 0, end: 0 }

    const cursorPos = inputRef.current.selectionStart || 0
    const text = value

    // Find word boundaries (space or start/end of string)
    let start = cursorPos
    let end = cursorPos

    // Move start backwards to find word start
    while (start > 0 && text[start - 1] !== ' ') {
      start--
    }

    // Move end forwards to find word end
    while (end < text.length && text[end] !== ' ') {
      end++
    }

    return {
      word: text.substring(start, end),
      start,
      end,
    }
  }

  // Generate suggestions based on current input for all supported languages
  const generateSuggestions = (word: string): Suggestion[] => {
    if (!supportsAutocomplete) return []
    if (!word || word.trim() === '') return []

    switch (currentLanguageCode) {
      case 'ja': {
        // Japanese: romaji to kana conversion
        const hasRomaji = /[a-zA-Z]/.test(word)
        if (!hasRomaji) return []
        return buildKanaSuggestions(word).map((s) => ({ ...s, language: 'ja' as const }))
      }

      case 'es': {
        // Spanish: dictionary-based suggestions
        return buildSpanishSuggestions(word).map((s) => ({ ...s, language: 'es' as const }))
      }

      case 'zh': {
        // Mandarin: pinyin to character conversion
        // Only show suggestions for pinyin input (not for Chinese characters)
        if (containsChinese(word)) return []
        return buildMandarinSuggestions(word).map((s) => ({ ...s, language: 'zh' as const }))
      }

      default:
        return []
    }
  }

  // Update suggestions when value changes
  useEffect(() => {
    const { word } = getCurrentWord()
    const newSuggestions = generateSuggestions(word)

    setSuggestions(newSuggestions)
    setShowSuggestions(newSuggestions.length > 0 && !disabled)
    setSelectedIndex(0)
  }, [value, disabled, supportsAutocomplete, currentLanguageCode])

  // Handle key down for navigation and conversion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent form submission during IME composition
    if (isComposing && e.key === 'Enter') {
      e.preventDefault()
      return
    }

    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault()
        onSubmit()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % suggestions.length)
        break

      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
        break

      case 'Tab':
      case 'Enter':
        e.preventDefault()
        if (e.key === 'Tab' || (e.key === 'Enter' && showSuggestions)) {
          applySuggestion(suggestions[selectedIndex])
        } else {
          onSubmit()
        }
        break

      case 'Escape':
        e.preventDefault()
        setShowSuggestions(false)
        break
    }
  }

  // Apply selected suggestion
  const applySuggestion = (suggestion: Suggestion) => {
    const { start, end } = getCurrentWord()
    const newValue = value.substring(0, start) + suggestion.text + value.substring(end)
    onChange(newValue)

    // Move cursor to end of replaced word
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = start + suggestion.text.length
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos)
        inputRef.current.focus()
      }
    }, 0)

    setShowSuggestions(false)
  }

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch English translations for suggestions (where needed) so users know what they're selecting
  useEffect(() => {
    if (disabled || suggestions.length === 0) return

    // For Spanish and Mandarin, translations are already included in suggestions
    // For Japanese, we need to fetch translations for kana suggestions (not romaji)
    if (currentLanguageCode !== 'ja') return

    const convertibleSuggestions = suggestions.filter((suggestion) => suggestion.type !== 'romaji')
    const pendingByCanonical = new Map<string, Suggestion[]>()

    convertibleSuggestions.forEach((suggestion) => {
      if (translationCacheRef.current[suggestion.text]) {
        return
      }

      const canonical = normalizeWordForTranslation(suggestion.text)

      if (canonicalTranslationCacheRef.current[canonical]) {
        translationCacheRef.current[suggestion.text] = canonicalTranslationCacheRef.current[canonical]
        setTranslationState((prev) => ({
          ...prev,
          [suggestion.text]: { loading: false, translation: canonicalTranslationCacheRef.current[canonical] },
        }))
        return
      }

      if (!pendingByCanonical.has(canonical)) {
        pendingByCanonical.set(canonical, [])
      }
      pendingByCanonical.get(canonical)!.push(suggestion)
    })

    const canonicalsToFetch = Array.from(pendingByCanonical.entries()).filter(
      ([canonical]) => !loadingCanonicalsRef.current.has(canonical)
    )

    if (canonicalsToFetch.length === 0) return

    let isCancelled = false
    const userIdValue = (userId ?? '1').toString()
    const sessionIdValue = sessionId ? sessionId.toString() : ''

    canonicalsToFetch.forEach(([, suggestionList]) => {
      suggestionList.forEach((suggestion) => {
        setTranslationState((prev) => ({
          ...prev,
          [suggestion.text]: { ...prev[suggestion.text], loading: true, error: false },
        }))
      })
    })

    canonicalsToFetch.forEach(([canonical]) => {
      loadingCanonicalsRef.current.add(canonical)
    })

    const fetchTranslations = async () => {
      await Promise.all(
        canonicalsToFetch.map(async ([canonical, suggestionList]) => {
          try {
            const response = await translateWordAPI({
              word: canonical,
              userId: userIdValue as any,
              sessionId: sessionIdValue,
              targetLanguage: currentLanguageCode || 'ja',
              skipFlashcardCreate: true, // Don't create flashcards for autocomplete suggestions
            })

            if (isCancelled) return

            canonicalTranslationCacheRef.current[canonical] = response.translation
            suggestionList.forEach((suggestion) => {
              translationCacheRef.current[suggestion.text] = response.translation
            })

            setTranslationState((prev) => {
              const nextState = { ...prev }
              suggestionList.forEach((suggestion) => {
                nextState[suggestion.text] = { loading: false, translation: response.translation }
              })
              return nextState
            })
          } catch (error) {
            if (isCancelled) return
            setTranslationState((prev) => {
              const nextState = { ...prev }
              suggestionList.forEach((suggestion) => {
                nextState[suggestion.text] = { loading: false, error: true }
              })
              return nextState
            })
          } finally {
            loadingCanonicalsRef.current.delete(canonical)
          }
        })
      )
    }

    fetchTranslations()

    return () => {
      isCancelled = true
    }
  }, [suggestions, disabled, userId, sessionId, currentLanguageCode])

  const getTypeLabel = (type: string, suggestion: Suggestion) => {
    // Japanese types
    if (type === 'hiragana') return 'ひらがな'
    if (type === 'katakana') return 'カタカナ'
    if (type === 'romaji') return 'Romaji'

    // Spanish types
    if (type === 'exact') return 'Exact match'
    if (type === 'partial') return 'Suggestion'
    if (type === 'correction') return 'Corrected'

    // Mandarin - show pinyin as the badge
    if ('pinyin' in suggestion && suggestion.language === 'zh') {
      return suggestion.pinyin
    }

    // Mandarin types
    if (type === 'variant') return 'Variant'

    return ''
  }

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      // Japanese
      case 'hiragana':
        return 'bg-blue-100 text-blue-700'
      case 'katakana':
        return 'bg-purple-100 text-purple-700'
      case 'romaji':
        return 'bg-gray-100 text-gray-700'

      // Spanish
      case 'exact':
        return 'bg-green-100 text-green-700'
      case 'partial':
        return 'bg-blue-100 text-blue-700'
      case 'correction':
        return 'bg-amber-100 text-amber-700'

      // Mandarin
      case 'variant':
        return 'bg-purple-100 text-purple-700'

      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getTranslationDisplay = (suggestion: Suggestion) => {
    // Spanish and Mandarin have translations built-in
    if ('translation' in suggestion && (suggestion.language === 'es' || suggestion.language === 'zh')) {
      return { text: suggestion.translation, variant: 'translation' as const }
    }

    // Japanese romaji doesn't need translation
    if (suggestion.type === 'romaji') return null

    // Japanese translations are fetched dynamically
    if (translationCacheRef.current[suggestion.text]) {
      return { text: translationCacheRef.current[suggestion.text], variant: 'translation' as const }
    }

    const status = translationState[suggestion.text]
    if (status?.loading) {
      return { text: 'Translating...', variant: 'loading' as const }
    }

    if (status?.error) {
      return { text: 'Translation unavailable', variant: 'error' as const }
    }

    return null
  }

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-input)] text-[var(--color-text)] placeholder:text-[var(--color-text-secondary)] focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow disabled:bg-[var(--color-surface-secondary)] disabled:text-[var(--color-text-secondary)] disabled:cursor-not-allowed"
        autoComplete="off"
        lang={currentLanguageCode || 'ja'}
      />

      {/* Suggestions dropdown - positioned above input */}
      {showSuggestions && suggestions.length > 0 && supportsAutocomplete && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl overflow-hidden backdrop-blur"
        >
          <div className="py-1">
            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] bg-[var(--color-surface-secondary)] border-b border-[var(--color-border)]">
              Press Tab or arrow keys to select, Enter to convert
            </div>
            {suggestions.map((suggestion, index) => {
              const translationDisplay = getTranslationDisplay(suggestion)
              const translationClass = translationDisplay
                ? translationDisplay.variant === 'translation'
                  ? 'text-[var(--color-text-secondary)]'
                  : translationDisplay.variant === 'loading'
                  ? 'text-[var(--color-text-secondary)] italic'
                  : 'text-error-500'
                : ''
              const autoCorrectNote = 'autoCorrectedFrom' in suggestion ? suggestion.autoCorrectedFrom : undefined
              const correctedFromNote = 'correctedFrom' in suggestion ? suggestion.correctedFrom : undefined

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => applySuggestion(suggestion)}
                  className={`w-full px-4 py-3 text-left rounded-xl transition-colors ${
                    index === selectedIndex ? 'bg-primary-50 border border-primary-200 shadow-sm' : 'hover:bg-[var(--color-surface-secondary)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium" lang={currentLanguageCode || 'ja'}>{suggestion.text}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${getTypeBadgeColor(
                        suggestion.type
                      )}`}
                    >
                      {getTypeLabel(suggestion.type, suggestion)}
                    </span>
                  </div>
                  {translationDisplay && (
                    <p className={`mt-1 text-sm ${translationClass}`}>{translationDisplay.text}</p>
                  )}
                  {autoCorrectNote && (
                    <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                      Auto-corrected from "{autoCorrectNote}"
                    </p>
                  )}
                  {correctedFromNote && (
                    <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                      Corrected from "{correctedFromNote}"
                    </p>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
